import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth-store';

import { LandingMarketingShell, LandingReveal } from './landing-shared';
import './landing.css';

/**
 * Public entry point for account deletion — linked from the footer, privacy policy,
 * and app store compliance pages.
 */
export const DeleteMePage = () => {
  const { t } = useTranslation('landing');
  const status = useAuthStore((state) => state.status);

  const signedIn = status === 'authenticated';

  return (
    <LandingMarketingShell signedIn={signedIn}>
      <main className="text-on-brand">
        <LandingReveal as="section" className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-20">
          <p className="text-caption font-semibold uppercase tracking-[0.2em] text-on-brand/75">
            {t('deleteMe.eyebrow')}
          </p>
          <h1 className="mt-3 text-balance text-[1.75rem] font-semibold tracking-tight sm:text-display">{t('deleteMe.title')}</h1>
          <p className="mt-4 text-body-lg text-on-brand/90">{t('deleteMe.intro')}</p>

          <div className="mt-8 space-y-6 rounded-xl border border-border bg-surface p-6 text-fg">
            <div>
              <h2 className="text-section-title font-semibold text-fg">{t('deleteMe.removesTitle')}</h2>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-body text-fg-secondary">
                {(t('deleteMe.removesItems', { returnObjects: true }) as string[]).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-section-title font-semibold text-fg">{t('deleteMe.keepsTitle')}</h2>
              <p className="mt-2 text-body text-fg-secondary">{t('deleteMe.keepsBody')}</p>
            </div>

            <div className="flex gap-3 rounded-lg border border-warning/30 bg-warning/5 px-4 py-3">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
              <p className="text-body-sm text-fg">{t('deleteMe.ownerNotice')}</p>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button variant="outline" className="landing-free-plans-cta-secondary" asChild>
              <Link to="/">{t('deleteMe.backHome')}</Link>
            </Button>
            {signedIn ? (
              <Button
                className="bg-on-brand text-brand shadow-e2 hover:bg-on-brand/90 active:bg-on-brand/85"
                asChild
              >
                <Link to="/account/delete">{t('deleteMe.continueSignedIn')}</Link>
              </Button>
            ) : (
              <Button
                className="bg-on-brand text-brand shadow-e2 hover:bg-on-brand/90 active:bg-on-brand/85"
                asChild
              >
                <Link to="/sign-in" state={{ from: '/account/delete' }}>
                  {t('deleteMe.signInToContinue')}
                </Link>
              </Button>
            )}
          </div>
        </LandingReveal>
      </main>
    </LandingMarketingShell>
  );
};
